/**
 * SupplierRollOffBolPage.jsx
 * ──────────────────────────
 * Screen 8 — Roll-Off Pickup Confirmation.
 * Route: /supplier/disposal/:projectId/rolloff/:bolId/pickup
 *
 * Reads location.state for { bol, project, loadedTools, isAmendedBol, sessionKey }.
 * Two actions: "Get Driver's Signature" or "Close Without Signature".
 */

import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../shared/context/UserContext';
import { useDisposalOfflineQueue } from '../../hooks/useDisposalOfflineQueue';
import SupplierLayout from '../../layouts/SupplierLayout';
import './SupplierRollOffBolPage.css';

export default function SupplierRollOffBolPage() {
  const { projectId, bolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { callerEmail } = useUser();
  const { queueBolClose, drainDisposalQueue } = useDisposalOfflineQueue(callerEmail);

  const { bol, project, loadedTools, isAmendedBol, sessionKey } = location.state || {};

  const [closing, setClosing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const bolNumber = bol?.bol_number || bolId?.substring(0, 12) || '—';

  const totalWeight = useMemo(() => {
    if (!loadedTools) return 0;
    return loadedTools.reduce((sum, t) => sum + (t.estimated_weight || t.weight_lbs || 0), 0);
  }, [loadedTools]);

  const toolCount = loadedTools?.length || 0;

  const backUrl = `/supplier/disposal/${projectId}/rolloff/${bolId}`;

  // ── Close without signature ────────────────────────────────────────
  async function handleCloseWithoutSignature() {
    setClosing(true);
    try {
      await queueBolClose({
        bol_id: bolId,
        project_id: projectId,
        closed_by: callerEmail,
        signature_image_url: null,
        driver_name_printed: null,
        estimated_weight_lbs: totalWeight,
      });

      // Clear session
      const key = sessionKey || `tmg_rolloff_session_${projectId}`;
      try { sessionStorage.removeItem(key); } catch { /* ignore */ }

      await drainDisposalQueue(callerEmail);
      navigate('/supplier/disposal', { replace: true });
    } catch (err) {
      console.error('Close shipment failed:', err);
      setClosing(false);
      setConfirmOpen(false);
    }
  }

  // ── Navigate to signature screen ───────────────────────────────────
  function handleGetSignature() {
    navigate(`/supplier/disposal/${projectId}/bol/${bolId}/sign`, {
      state: {
        ...location.state,
        sessionKey: sessionKey || `tmg_rolloff_session_${projectId}`,
      },
    });
  }

  // Missing state
  if (!bol || !project) {
    return (
      <SupplierLayout>
        <div className="rob-header">
          <button className="rob-back" onClick={() => navigate('/supplier/disposal')}>&larr;</button>
          <span className="rob-header-title">Pickup Confirmation</span>
          <span className="rob-header-logo">TMG</span>
        </div>
        <div className="rob-state">Session data not found. Please go back and try again.</div>
        <div style={{ textAlign: 'center', padding: 16 }}>
          <button className="rob-btn-ghost" onClick={() => navigate('/supplier/disposal')}>
            &larr; Back to Projects
          </button>
        </div>
      </SupplierLayout>
    );
  }

  return (
    <SupplierLayout>
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="rob-header">
        <button className="rob-back" onClick={() => navigate(backUrl)}>&larr;</button>
        <span className="rob-header-title">Pickup Confirmation</span>
        <span className="rob-header-logo">TMG</span>
      </div>

      {/* ── Amended banner ────────────────────────────────────────────── */}
      {isAmendedBol && (
        <div className="rob-amended-banner">
          AMENDED — {bolNumber}-A
        </div>
      )}

      {/* ── Scrollable content ────────────────────────────────────────── */}
      <div className="rob-scroll">
        {/* ── Pickup summary card ───────────────────────────────────── */}
        <div className="rob-summary-card">
          <div className="rob-summary-label">ROLL-OFF PICKUP SUMMARY</div>

          <div className="rob-summary-grid">
            <div className="rob-field">
              <span className="rob-field-label">BOL Number</span>
              <span className="rob-field-value rob-mono">
                {isAmendedBol ? `${bolNumber}-A` : bolNumber}
              </span>
            </div>
            <div className="rob-field">
              <span className="rob-field-label">Container #</span>
              <span className="rob-field-value">{bol.truck_number || '—'}</span>
            </div>
            <div className="rob-field">
              <span className="rob-field-label">Hauler</span>
              <span className="rob-field-value">{bol.hauler_company || '—'}</span>
            </div>
            <div className="rob-field">
              <span className="rob-field-label">Advisory Weight</span>
              <span className="rob-field-value">
                {bol.advisory_weight_lbs ? `${Number(bol.advisory_weight_lbs).toLocaleString()} lbs` : '—'}
              </span>
            </div>
          </div>

          <div className="rob-divider" />

          <div className="rob-totals">
            <div className="rob-total-line">Tools on this load: <strong>{toolCount} tool{toolCount !== 1 ? 's' : ''}</strong></div>
            <div className="rob-total-line">Total estimated weight: <strong>{totalWeight.toLocaleString()} lbs</strong></div>
          </div>
        </div>

        {/* ── Tool list (read-only) ─────────────────────────────────── */}
        <div className="rob-tool-section">
          <div className="rob-tool-section-label">Tools in this container</div>
          <div className="rob-tool-list">
            {loadedTools && loadedTools.length > 0 ? (
              loadedTools.map(t => {
                const w = t.estimated_weight || t.weight_lbs || 0;
                return (
                  <div key={t.id || t.tool_id} className="rob-tool-row">
                    <span className="rob-tool-id">{t.supplier_tool_id || t.id || t.tool_id}</span>
                    <span className="rob-tool-weight">
                      {w ? `${Number(w).toLocaleString()} lbs` : '—'}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="rob-tool-empty">No tools loaded.</div>
            )}
          </div>
        </div>
      </div>

      {/* ── Action buttons — sticky bottom ────────────────────────────── */}
      <div className="rob-actions">
        <button className="rob-btn-secondary" onClick={handleGetSignature} disabled={closing}>
          Get Driver's Signature
        </button>
        <button className="rob-btn-primary" onClick={() => setConfirmOpen(true)} disabled={closing}>
          {closing ? 'Closing...' : 'Close Without Signature'}
        </button>
      </div>

      {/* ── Confirmation dialog ───────────────────────────────────────── */}
      {confirmOpen && (
        <div className="rob-confirm-overlay" onClick={() => !closing && setConfirmOpen(false)}>
          <div className="rob-confirm-card" onClick={e => e.stopPropagation()}>
            <div className="rob-confirm-text">
              Close this shipment without a driver signature?
            </div>
            <div className="rob-confirm-btns">
              <button className="rob-confirm-cancel" onClick={() => setConfirmOpen(false)} disabled={closing}>
                Cancel
              </button>
              <button className="rob-confirm-close" onClick={handleCloseWithoutSignature} disabled={closing}>
                {closing ? 'Closing...' : 'Close Shipment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </SupplierLayout>
  );
}
